import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  FiAlertTriangle,
  FiCamera,
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiHash,
  FiMapPin,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiTruck,
  FiUpload,
  FiUser,
  FiWifi,
  FiX
} from "react-icons/fi";
import { isAuthenticated } from "../auth";
import Layout from "../Layout/Layout";
import {
  deleteDeliveryProof,
  listDeliveryOrders,
  loadDeliveryProof,
  updateDeliveryOrder
} from "../auth/deliveryApi";
import ShippingProviderPanel from "../pageInside/ShippingProviderPanel";
import "./Delivery.css";
import { elakayAutoText } from "../pageInside/dataElakay";
import { assertRequestFits, prepareUpload } from "../../utils/uploadPolicy";


const CARRIERS = [
  { group: "USA", code: "usps", name: "USPS", type: "national" },
  { group: "USA", code: "ups", name: "UPS", type: "national" },
  { group: "USA", code: "fedex", name: "FedEx", type: "national" },
  { group: "USA", code: "dhl_ecommerce", name: "DHL eCommerce", type: "national" },
  { group: "USA", code: "ontrac", name: "OnTrac", type: "regional" },
  { group: "Canada", code: "canada_post", name: "Canada Post", type: "national" },
  { group: "Canada", code: "purolator", name: "Purolator", type: "national" },
  { group: "Canada", code: "canpar", name: "Canpar Express", type: "national" },
  { group: "Canada", code: "loomis", name: "Loomis Express", type: "national" },
  { group: "Canada", code: "fedex_canada", name: "FedEx Canada", type: "national" },
  { group: "Canada", code: "ups_canada", name: "UPS Canada", type: "national" },
  {
    get group() { return elakayAutoText("h_8bb749a035fbbe", "Flexible"); },
    code: "local_private",
    get name() { return elakayAutoText("h_a0ee7807b622b8", "Particulier / LLC locale"); },
    type: "local"
  },
  {
    get group() { return elakayAutoText("h_8bb749a035fbbe", "Flexible"); },
    code: "other",
    get name() { return elakayAutoText("h_a89f7e83942827", "Autre transporteur"); },
    type: "other"
  }
];

const STATUSES = [
  { value: "pending_pickup", get label() { return elakayAutoText("h_c0cb8c7e63ce0f", "En attente de collecte"); }, tone: "gray" },
  { value: "label_created", get label() { return elakayAutoText("h_82648433ce090c", "Etiquette creee"); }, tone: "blue" },
  { value: "picked_up", get label() { return elakayAutoText("h_0b84c431227a68", "Colis collecte"); }, tone: "blue" },
  { value: "accepted", get label() { return elakayAutoText("h_f78fb436c890dc", "Accepte par le transporteur"); }, tone: "blue" },
  { value: "in_transit", get label() { return elakayAutoText("h_5b090eba401d55", "En transit"); }, tone: "blue" },
  { value: "out_for_delivery", get label() { return elakayAutoText("h_ac14b66ea30cc5", "En cours de livraison"); }, tone: "amber" },
  { value: "attempted", get label() { return elakayAutoText("h_2cd3b78dfa010d", "Tentative de livraison"); }, tone: "amber" },
  { value: "delivered", get label() { return elakayAutoText("h_5c0c4cd5668cbb", "Livre"); }, tone: "green" },
  { value: "exception", get label() { return elakayAutoText("h_a1d92b2a52ec51", "Incident / exception"); }, tone: "red" },
  { value: "held", get label() { return elakayAutoText("h_43ff1778b55a2f", "Colis retenu"); }, tone: "amber" },
  { value: "returned", get label() { return elakayAutoText("h_ff90889efb95fd", "Retourne"); }, tone: "red" },
  { value: "cancelled", get label() { return elakayAutoText("h_273949a9f72e72", "Annule"); }, tone: "red" }
];

const PROOF_KINDS = [
  { value: "package", get label() { return elakayAutoText("h_4abb02e27c6cbd", "Colis"); } },
  { value: "pickup", get label() { return elakayAutoText("h_aa7d0813d15fd1", "Collecte"); } },
  { value: "dropoff", get label() { return elakayAutoText("h_908d3d64c884d2", "Depot"); } },
  { value: "door", get label() { return elakayAutoText("h_87d0dcd67d17f6", "Porte / lieu"); } },
  { value: "recipient", get label() { return elakayAutoText("h_56579042c7742c", "Destinataire"); } },
  { value: "damage", get label() { return elakayAutoText("h_278455e15b1b0f", "Dommage"); } },
  { value: "signature", get label() { return elakayAutoText("h_2f32be1dc74166", "Signature"); } },
  { value: "other", get label() { return elakayAutoText("h_43dacf9e4764b7", "Autre"); } }
];

const QUICK_STATUSES = [
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "exception"
];

const EMPTY_SUMMARY = {
  total: 0,
  pending: 0,
  inTransit: 0,
  outForDelivery: 0,
  delivered: 0,
  exceptions: 0
};

const emptyForm = {
  carrierCode: "",
  carrierName: "",
  carrierType: "national",
  trackingNumber: "",
  trackingUrl: "",
  serviceLevel: "",
  packageCount: 1,
  status: "pending_pickup",
  recipientName: "",
  signatureName: "",
  driverNotes: "",
  eventNote: "",
  exceptionReason: "",
  locationLabel: "",
  estimatedDeliveryAt: ""
};

const statusMeta = (value) => (
  STATUSES.find((status) => status.value === value) || STATUSES[0]
);

const shortId = (value) => {
  const text = String(value || "");
  return text.length > 10 ? text.slice(-10).toUpperCase() : text.toUpperCase();
};

const dateTime = (value) => {
  if (!value) return "Non disponible";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Non disponible";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const dateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const buildTrackingUrl = (carrierCode, trackingNumber) => {
  const tracking = encodeURIComponent(String(trackingNumber || "").trim());
  if (!tracking) return "";

  const builders = {
    usps: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${tracking}`,
    ups: `https://www.ups.com/track?tracknum=${tracking}`,
    ups_canada: `https://www.ups.com/track?tracknum=${tracking}`,
    fedex: `https://www.fedex.com/fedextrack/?trknbr=${tracking}`,
    fedex_canada: `https://www.fedex.com/fedextrack/?trknbr=${tracking}`,
    dhl_ecommerce: `https://webtrack.dhlglobalmail.com/?trackingnumber=${tracking}`,
    ontrac: `https://www.ontrac.com/tracking/?number=${tracking}`,
    canada_post: `https://www.canadapost-postescanada.ca/track-reperage/en#/details/${tracking}`,
    purolator: `https://www.purolator.com/en/shipping/tracker?pin=${tracking}`,
    canpar: `https://www.canpar.com/en/track/TrackingAction.do?reference=${tracking}`
  };

  return builders[carrierCode] || "";
};

const addressLine = (address = {}) => {
  const person = [address.firstName, address.lastName].filter(Boolean).join(" ");
  const street = [address.line1, address.line2].filter(Boolean).join(", ");
  const city = [address.city, address.state, address.postal_code].filter(Boolean).join(", ");
  return [person, street, city, address.country].filter(Boolean);
};

function StatusBadge({ value }) {
  const meta = statusMeta(value);
  return (
    <span className={`delivery-status delivery-status-${meta.tone}`}>
      {meta.label}
    </span>
  );
}

function Delivery({ infoSource }) {
  const { token: authToken } = isAuthenticated();
  const token = infoSource?.token || authToken;
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scannerTimerRef = useRef(null);
  const scannerBusyRef = useRef(false);
  const pendingPhotosRef = useRef([]);
  const proofUrlsRef = useRef({});

  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [lastScan, setLastScan] = useState("");
  const [location, setLocation] = useState(null);
  const [locationBusy, setLocationBusy] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [proofKind, setProofKind] = useState("package");
  const [proofNote, setProofNote] = useState("");
  const [proofUrls, setProofUrls] = useState({});

  const groupedCarriers = useMemo(() => (
    CARRIERS.reduce((groups, carrier) => {
      if (!groups[carrier.group]) groups[carrier.group] = [];
      groups[carrier.group].push(carrier);
      return groups;
    }, {})
  ), []);

  const resetFormFromOrder = useCallback((order) => {
    const delivery = order?.delivery || {};
    const legacy = order?.shipmentInfo || {};
    const selectedCarrier = CARRIERS.find((carrier) => (
      carrier.code === delivery.carrierCode
      || carrier.name.toLowerCase() === String(delivery.carrierName || legacy.carrier || "").toLowerCase()
    ));

    setForm({
      carrierCode: delivery.carrierCode || selectedCarrier?.code || "",
      carrierName: delivery.carrierName || legacy.carrier || selectedCarrier?.name || "",
      carrierType: delivery.carrierType || selectedCarrier?.type || "national",
      trackingNumber: delivery.trackingNumber || legacy.tracking || "",
      trackingUrl: delivery.trackingUrl || legacy.urlTracking || "",
      serviceLevel: delivery.serviceLevel || "",
      packageCount: delivery.packageCount || 1,
      status: delivery.status || "pending_pickup",
      recipientName: delivery.recipientName || "",
      signatureName: delivery.signatureName || "",
      driverNotes: delivery.driverNotes || "",
      eventNote: "",
      exceptionReason: delivery.exceptionReason || "",
      locationLabel: "",
      estimatedDeliveryAt: dateInputValue(delivery.estimatedDeliveryAt)
    });
  }, []);

  const clearProofUrls = useCallback(() => {
    Object.values(proofUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    proofUrlsRef.current = {};
    setProofUrls({});
  }, []);

  const loadProofImages = useCallback(async (order) => {
    clearProofUrls();
    const proofs = order?.delivery?.proofs || [];
    if (!proofs.length || !token) return;

    const entries = await Promise.all(proofs.map(async (proof) => {
      try {
        const url = await loadDeliveryProof(
          token,
          order.orderId,
          order.orderProductId,
          proof._id
        );
        return [String(proof._id), url];
      } catch {
        return [String(proof._id), ""];
      }
    }));

    const nextUrls = Object.fromEntries(entries.filter(([, url]) => url));
    proofUrlsRef.current = nextUrls;
    setProofUrls(nextUrls);
  }, [clearProofUrls, token]);

  const selectOrder = useCallback((order) => {
    setSelected(order);
    resetFormFromOrder(order);
    setMessage("");
    setError("");
    setLastScan("");
    loadProofImages(order);
  }, [loadProofImages, resetFormFromOrder]);

  const loadOrders = useCallback(async (queryValue = search) => {
    if (!token) return null;
    setLoading(true);
    setError("");

    try {
      const data = await listDeliveryOrders(token, {
        q: queryValue,
        status: statusFilter,
        carrier: carrierFilter,
        limit: 100
      });

      const nextOrders = data.orders || [];
      setOrders(nextOrders);
      setSummary(data.summary || EMPTY_SUMMARY);

      setSelected((current) => {
        if (!current) return current;
        return nextOrders.find((item) => (
          String(item.orderId) === String(current.orderId)
          && String(item.orderProductId) === String(current.orderProductId)
        )) || current;
      });

      return data;
    } catch (loadError) {
      setError(loadError.message || elakayAutoText("h_c7a04cad7da2a8", "Impossible de charger les livraisons."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [carrierFilter, search, statusFilter, token]);

  useEffect(() => {
    loadOrders("");
    // The filters intentionally control the queue refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, carrierFilter, token]);

  useEffect(() => () => {
    pendingPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
    Object.values(proofUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (scannerTimerRef.current) clearInterval(scannerTimerRef.current);
  }, []);

  const findScannedOrder = useCallback(async (value) => {
    const code = String(value || "").trim();
    if (!code) return;

    const objectIds = code.match(/[a-f0-9]{24}/gi) || [];
    const lookupValue = objectIds[0] || code;
    setLastScan(code);
    setSearch(lookupValue);
    const data = await loadOrders(lookupValue);
    const matches = data?.orders || [];

    if (matches.length) {
      const exactItem = objectIds[1]
        ? matches.find((item) => String(item.orderProductId).toLowerCase() === objectIds[1].toLowerCase())
        : null;
      selectOrder(exactItem || matches[0]);
      setMessage(elakayAutoText("h_6df162bd6eb499", "Commande trouvee avec le scan {value0}.", { value0: code }));
    } else {
      setError(elakayAutoText("h_cd6092fb7e3fa4", "Aucune commande trouvee pour {value0}.", { value0: code }));
    }
  }, [loadOrders, selectOrder]);

  const stopScanner = useCallback(() => {
    if (scannerTimerRef.current) {
      clearInterval(scannerTimerRef.current);
      scannerTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    scannerBusyRef.current = false;
  }, []);

  useEffect(() => {
    if (!scannerOpen) {
      stopScanner();
      return undefined;
    }

    let cancelled = false;

    const start = async () => {
      setScannerError("");

      if (!navigator.mediaDevices?.getUserMedia) {
        setScannerError("La camera n'est pas disponible. Utilisez la saisie manuelle.");
        return;
      }

      if (!window.BarcodeDetector) {
        setScannerError(
          "La detection automatique n'est pas supportee par ce navigateur. Utilisez un lecteur USB/Bluetooth ou la saisie manuelle."
        );
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const detector = new window.BarcodeDetector();
        scannerTimerRef.current = setInterval(async () => {
          if (!videoRef.current || scannerBusyRef.current) return;
          scannerBusyRef.current = true;

          try {
            const codes = await detector.detect(videoRef.current);
            const rawValue = codes?.[0]?.rawValue;
            if (rawValue) {
              stopScanner();
              setScannerOpen(false);
              await findScannedOrder(rawValue);
            }
          } catch {
            // Detection continues on the next frame.
          } finally {
            scannerBusyRef.current = false;
          }
        }, 650);
      } catch (cameraError) {
        setScannerError(
          cameraError?.name === "NotAllowedError"
            ? elakayAutoText("h_2e74269b565c27", "Autorisez la caméra pour scanner automatiquement.")
            : elakayAutoText("h_f850db26810861", "Impossible d'ouvrir la caméra. Utilisez la saisie manuelle.")
        );
      }
    };

    start();

    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [findScannedOrder, scannerOpen, stopScanner]);

  const onCarrierChange = (event) => {
    const carrierCode = event.target.value;
    const carrier = CARRIERS.find((item) => item.code === carrierCode);
    const generatedUrl = buildTrackingUrl(carrierCode, form.trackingNumber);

    setForm((current) => ({
      ...current,
      carrierCode,
      carrierName: carrier?.name || "",
      carrierType: carrier?.type || "other",
      trackingUrl: generatedUrl || current.trackingUrl
    }));
  };

  const onTrackingChange = (event) => {
    const trackingNumber = event.target.value.trimStart();
    setForm((current) => ({
      ...current,
      trackingNumber,
      trackingUrl: buildTrackingUrl(current.carrierCode, trackingNumber) || current.trackingUrl
    }));
  };

  const addPhotos = (event) => {
    const available = Math.max(0, 4 - pendingPhotos.length);
    const files = Array.from(event.target.files || [])
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, available);

    if (!files.length) return;

    const next = files.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      file,
      url: URL.createObjectURL(file),
      kind: proofKind
    }));

    setPendingPhotos((current) => {
      const updated = [...current, ...next];
      pendingPhotosRef.current = updated;
      return updated;
    });
    event.target.value = "";
  };

  const removePendingPhoto = (photoId) => {
    setPendingPhotos((current) => {
      const removed = current.find((photo) => photo.id === photoId);
      if (removed) URL.revokeObjectURL(removed.url);
      const updated = current.filter((photo) => photo.id !== photoId);
      pendingPhotosRef.current = updated;
      return updated;
    });
  };

  const captureLocation = () => {
    setLocationBusy(true);
    setError("");

    if (!navigator.geolocation) {
      setLocationBusy(false);
      setError(elakayAutoText("h_90f282d1cec22b", "La geolocalisation n'est pas disponible sur cet appareil."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setLocationBusy(false);
      },
      (locationError) => {
        setError(locationError.message || elakayAutoText("h_f03c47bcfc06db", "Impossible de recuperer la position."));
        setLocationBusy(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  };

  const saveUpdate = async () => {
    if (!selected) return;

    if (!form.carrierCode && !form.carrierName) {
      setError(elakayAutoText("h_a1e622be7ae970", "Selectionnez ou saisissez un transporteur."));
      return;
    }

    if (form.status === "delivered" && !form.recipientName.trim()) {
      setError(elakayAutoText("h_dfeb32e8968a3a", "Indiquez le nom du destinataire ou la personne ayant recu le colis."));
      return;
    }

    if (["exception", "attempted", "held"].includes(form.status) && !form.exceptionReason.trim()) {
      setError(elakayAutoText("h_f9cf1823a54347", "Ajoutez la raison de l'incident ou de la tentative."));
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== undefined && value !== null) body.append(key, value);
      });

      body.append("source", lastScan ? "scanner" : "manual");
      body.append("scanCode", lastScan);
      body.append("occurredAt", new Date().toISOString());
      body.append("proofNote", proofNote);
      body.append("proofKinds", JSON.stringify(pendingPhotos.map((photo) => photo.kind)));

      if (location) {
        body.append("latitude", location.latitude);
        body.append("longitude", location.longitude);
        body.append("accuracy", location.accuracy);
      }

      // Phone photos (3–6 MB each) are re-encoded before sending so the four
      // proofs fit the server and proxy limits (5 MB each, one request).
      const proofFiles = await Promise.all(pendingPhotos.map((photo) => prepareUpload("deliveryProof", photo.file)));
      assertRequestFits(proofFiles, "/elakay_api/delivery/order");
      proofFiles.forEach((file) => body.append("proofs", file));

      const data = await updateDeliveryOrder(
        token,
        selected.orderId,
        selected.orderProductId,
        body
      );

      const updatedOrder = data.order;
      setSelected(updatedOrder);
      resetFormFromOrder(updatedOrder);
      setOrders((current) => current.map((order) => (
        String(order.orderId) === String(updatedOrder.orderId)
        && String(order.orderProductId) === String(updatedOrder.orderProductId)
          ? updatedOrder
          : order
      )));
      pendingPhotos.forEach((photo) => URL.revokeObjectURL(photo.url));
      pendingPhotosRef.current = [];
      setPendingPhotos([]);
      setProofNote("");
      setLastScan("");
      setMessage(elakayAutoText("h_a85f8fac9fae52", "La commande et l'historique de livraison ont ete mis a jour."));
      await loadProofImages(updatedOrder);
      await loadOrders(search);
    } catch (saveError) {
      setError(saveError.message || elakayAutoText("h_df0ff23cb03805", "La mise a jour a echoue."));
    } finally {
      setSaving(false);
    }
  };

  const removeExistingProof = async (proofId) => {
    if (!selected || !window.confirm(elakayAutoText("h_df7e05821c9461", "Supprimer cette preuve photo ?"))) return;

    setSaving(true);
    setError("");
    try {
      const data = await deleteDeliveryProof(
        token,
        selected.orderId,
        selected.orderProductId,
        proofId
      );
      setSelected(data.order);
      await loadProofImages(data.order);
      setMessage(elakayAutoText("h_80673ad5f1e088", "Preuve photo supprimee."));
    } catch (deleteError) {
      setError(deleteError.message || elakayAutoText("h_b321aa1a9f2b3b", "Impossible de supprimer cette preuve."));
    } finally {
      setSaving(false);
    }
  };

  const submitSearch = async (event) => {
    event.preventDefault();
    const data = await loadOrders(search);
    if ((data?.orders || []).length === 1) selectOrder(data.orders[0]);
  };

  const stats = [
    { label: elakayAutoText("h_d4df129159e7bd", "Total actif"), value: summary.total, icon: FiPackage },
    { label: elakayAutoText("h_11107fd0c8ce99", "A collecter"), value: summary.pending, icon: FiClock },
    { label: elakayAutoText("h_5b090eba401d55", "En transit"), value: summary.inTransit, icon: FiTruck },
    { label: elakayAutoText("h_c296e9499783ab", "En livraison"), value: summary.outForDelivery, icon: FiMapPin },
    { label: elakayAutoText("h_612cda92a2b0e4", "Livres"), value: summary.delivered, icon: FiCheckCircle }
  ];

  return (
    <Layout
      infoSource={infoSource}
      title={elakayAutoText("h_cef6a91aa99a81", "Delivery Center")}
      description={elakayAutoText("h_f04c9f17b41e56", "Gestion des livraisons, scans et preuves photo")}
      className="container-fluid"
      SliderPunShow={false}
      loading={false}
      {...infoSource?.allSettings?.layout}
    >
      <main className="delivery-page">
        <div className="delivery-shell">
        <header className="delivery-hero">
          <div>
            <div className="delivery-eyebrow">
              <FiTruck />{elakayAutoText("h_327e0eaf2c5865", "Operations USA & Canada")}</div>
            <h1>{elakayAutoText("h_cef6a91aa99a81", "Delivery Center")}</h1>
            <p>{elakayAutoText("h_2d3adfa3b4eb62", "Scannez une commande, choisissez n'importe quel transporteur national, regional ou local, puis enregistrez chaque mouvement avec position, notes, destinataire et preuves photo.")}</p>
          </div>
          <div className="delivery-live-pill">
            <span className="delivery-live-dot" />{elakayAutoText("h_273f4201893eb2", "Mise a jour des commandes active")}</div>
        </header>

        <ShippingProviderPanel token={token} />

        <section className="delivery-stats" aria-label={elakayAutoText("h_234d8a2f00aa39", "Statistiques de livraison")}>
          {stats.map(({ label, value, icon: Icon }) => (
            <article className="delivery-stat" key={label}>
              <div className="delivery-stat-top">
                <span>{label}</span>
                <span className="delivery-stat-icon"><Icon /></span>
              </div>
              <strong>{value || 0}</strong>
            </article>
          ))}
        </section>

        <form className="delivery-toolbar" onSubmit={submitSearch}>
          <div className="delivery-search">
            <FiSearch />
            <input
              className="delivery-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={elakayAutoText("h_776418ac4c5253", "Scanner ou saisir: commande, article, tracking, client...")}
              autoComplete="off"
            />
          </div>
          <select
            className="delivery-select"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            aria-label={elakayAutoText("h_e05919829dc2c1", "Filtrer par statut")}
          >
            <option value="all">{elakayAutoText("h_2d5088af094b73", "Tous les statuts")}</option>
            {STATUSES.map((status) => (
              <option value={status.value} key={status.value}>{status.label}</option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="delivery-btn delivery-btn-secondary" type="submit">
              <FiSearch />{elakayAutoText("h_91f7d3e9dc3d2d", "Rechercher")}</button>
            <button
              className="delivery-btn delivery-btn-primary"
              type="button"
              onClick={() => setScannerOpen(true)}
            >
              <FiCamera />{elakayAutoText("h_c7ee483aa3d721", "Scanner")}</button>
          </div>
        </form>

        {scannerOpen && (
          <section className="delivery-panel" style={{ marginBottom: 16 }}>
            <div className="delivery-panel-head">
              <div>
                <div className="delivery-panel-title">{elakayAutoText("h_a74818bfa392d0", "Scanner automatique")}</div>
                <div className="delivery-panel-subtitle">{elakayAutoText("h_7a9d332da424c9", "Commande, article, numero de suivi, code-barres ou QR code")}</div>
              </div>
              <button
                type="button"
                className="delivery-btn delivery-btn-secondary"
                style={{ minHeight: 34, padding: "6px 10px" }}
                onClick={() => setScannerOpen(false)}
              >
                <FiX />{elakayAutoText("h_5ab4ec640d654d", "Fermer")}</button>
            </div>
            <div className="delivery-scanner" style={{ margin: 14 }}>
              <div className="delivery-scanner-stage">
                <video ref={videoRef} muted playsInline />
                {!scannerError && (
                  <div className="delivery-scan-frame">
                    <div className="delivery-scan-line" />
                  </div>
                )}
              </div>
              <div className="delivery-scanner-foot">
                <span>
                  {scannerError || elakayAutoText("h_c494d12154f14f", "Placez le code-barres ou QR code au centre.")}
                </span>
                {scannerError && (
                  <span>{elakayAutoText("h_e97cfa42fa9758", "La barre de recherche accepte aussi les lecteurs USB/Bluetooth.")}</span>
                )}
              </div>
            </div>
          </section>
        )}

        <div className="delivery-workspace">
          <section className="delivery-panel">
            <div className="delivery-panel-head">
              <div>
                <div className="delivery-panel-title">{elakayAutoText("h_77d8491f24fe26", "File de livraison")}</div>
                <div className="delivery-panel-subtitle">
                  {loading ? elakayAutoText("h_a209b664efa2dc", "Chargement...") : `${orders.length} article(s) affiche(s)`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 7 }}>
                <select
                  className="delivery-select"
                  style={{ minHeight: 36, padding: "6px 9px", fontSize: 10 }}
                  value={carrierFilter}
                  onChange={(event) => setCarrierFilter(event.target.value)}
                  aria-label={elakayAutoText("h_ae7eca31b40e3f", "Filtrer par transporteur")}
                >
                  <option value="all">{elakayAutoText("h_adb0ac724aed32", "Tous transporteurs")}</option>
                  {CARRIERS.map((carrier) => (
                    <option value={carrier.code} key={carrier.code}>{carrier.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="delivery-btn delivery-btn-secondary"
                  style={{ minHeight: 36, padding: "7px 10px" }}
                  onClick={() => loadOrders(search)}
                  aria-label={elakayAutoText("h_9d3b2a7d8b0d96", "Actualiser")}
                >
                  <FiRefreshCw className={loading ? "delivery-spin" : ""} />
                </button>
              </div>
            </div>

            <div className="delivery-order-list">
              {loading && !orders.length ? (
                <div className="delivery-empty">
                  <div>
                    <FiRefreshCw className="delivery-spin" size={28} />
                    <p>{elakayAutoText("h_f8d02adeec13b2", "Chargement de vos commandes...")}</p>
                  </div>
                </div>
              ) : orders.length ? (
                orders.map((order) => {
                  const active = selected
                    && String(selected.orderId) === String(order.orderId)
                    && String(selected.orderProductId) === String(order.orderProductId);
                  return (
                    <button
                      type="button"
                      className={`delivery-order-card${active ? " is-active" : ""}`}
                      key={`${order.orderId}_${order.orderProductId}`}
                      onClick={() => selectOrder(order)}
                    >
                      <img
                        className="delivery-order-image"
                        src={order.product?.image || infoSource?.imageEmptyelakay}
                        alt=""
                      />
                      <span style={{ minWidth: 0 }}>
                        <span className="delivery-order-name">{order.product?.name}</span>
                        <span className="delivery-order-meta">
                          <span>#{shortId(order.orderId)}</span>
                          <span>{order.delivery?.carrierName || order.shipmentInfo?.carrier || elakayAutoText("h_8fa9471e7b18cc", "Sans transporteur")}</span>
                          <span>{dateTime(order.delivery?.lastScannedAt || order.orderCreatedAt)}</span>
                        </span>
                      </span>
                      <StatusBadge value={order.delivery?.status || "pending_pickup"} />
                    </button>
                  );
                })
              ) : (
                <div className="delivery-empty">
                  <div>
                    <div className="delivery-empty-icon"><FiPackage size={28} /></div>
                    <strong>{elakayAutoText("h_f4e7da9219022e", "Aucune commande trouvee")}</strong>
                    <p>{elakayAutoText("h_f7c36d3360be28", "Modifiez les filtres ou scannez un autre code.")}</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="delivery-panel">
            {!selected ? (
              <div className="delivery-empty" style={{ minHeight: 560 }}>
                <div>
                  <div className="delivery-empty-icon"><FiWifi size={29} /></div>
                  <strong>{elakayAutoText("h_8b36e6f2886e10", "Selectionnez ou scannez une commande")}</strong>
                  <p>{elakayAutoText("h_0fd7fb5d289e62", "Le panneau de mise a jour affichera le transporteur, l'adresse, les preuves et l'historique complet.")}</p>
                </div>
              </div>
            ) : (
              <div className="delivery-detail">
                <div className="delivery-order-summary">
                  <article className="delivery-summary-card delivery-summary-product">
                    <img
                      src={selected.product?.image || infoSource?.imageEmptyelakay}
                      alt=""
                    />
                    <div>
                      <h2>{selected.product?.name}</h2>
                      <p>{elakayAutoText("h_666aceab2998e6", "Commande #")}{shortId(selected.orderId)}{elakayAutoText("h_5642dd058aba3e", "· Article #")}{shortId(selected.orderProductId)}
                      </p>
                      <p>{elakayAutoText("h_4b1c4b13f122a9", "Quantite")}{selected.product?.quantity || 1}
                        {selected.product?.variant ? ` · ${selected.product.variant}` : ""}
                      </p>
                    </div>
                  </article>
                  <article className="delivery-summary-card">
                    <div className="delivery-section-title" style={{ marginBottom: 7 }}>
                      <FiMapPin />{elakayAutoText("h_d42713493ca811", "Destination")}</div>
                    <div className="delivery-address">
                      {addressLine(selected.shippingAddress).map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </div>
                  </article>
                </div>

                {error && (
                  <div className="delivery-alert delivery-alert-error">
                    <FiAlertTriangle size={17} /> <span>{error}</span>
                  </div>
                )}
                {message && (
                  <div className="delivery-alert delivery-alert-success">
                    <FiCheckCircle size={17} /> <span>{message}</span>
                  </div>
                )}

                <section className="delivery-section">
                  <div className="delivery-section-head">
                    <div className="delivery-section-title"><FiTruck />{elakayAutoText("h_c7bfee111a04bc", "Expedition")}</div>
                    <StatusBadge value={form.status} />
                  </div>

                  <div className="delivery-quick-statuses">
                    {QUICK_STATUSES.map((value) => (
                      <button
                        type="button"
                        className={`delivery-quick-status${form.status === value ? " is-active" : ""}`}
                        key={value}
                        onClick={() => setForm((current) => ({ ...current, status: value }))}
                      >
                        {statusMeta(value).label}
                      </button>
                    ))}
                  </div>

                  <div className="delivery-form-grid delivery-form-grid-3">
                    <div className="delivery-field">
                      <label>{elakayAutoText("h_7adadb36bcb1ca", "Transporteur")}</label>
                      <select
                        className="delivery-select"
                        value={form.carrierCode}
                        onChange={onCarrierChange}
                      >
                        <option value="">{elakayAutoText("h_3888de7d5b4152", "Selectionner")}</option>
                        {Object.entries(groupedCarriers).map(([group, carriers]) => (
                          <optgroup label={group} key={group}>
                            {carriers.map((carrier) => (
                              <option value={carrier.code} key={carrier.code}>
                                {carrier.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    <div className="delivery-field">
                      <label>{elakayAutoText("h_a47bd5ae0aa2a0", "Numero de suivi")}</label>
                      <input
                        className="delivery-input"
                        value={form.trackingNumber}
                        onChange={onTrackingChange}
                        placeholder={elakayAutoText("h_4e40c545b8f9af", "Tracking number")}
                      />
                    </div>

                    <div className="delivery-field">
                      <label>{elakayAutoText("h_659499f38a5647", "Statut")}</label>
                      <select
                        className="delivery-select"
                        value={form.status}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          status: event.target.value
                        }))}
                      >
                        {STATUSES.map((status) => (
                          <option value={status.value} key={status.value}>{status.label}</option>
                        ))}
                      </select>
                    </div>

                    {["local_private", "other"].includes(form.carrierCode) && (
                      <div className="delivery-field">
                        <label>{elakayAutoText("h_5e692bacd56094", "Nom de la compagnie / LLC")}</label>
                        <input
                          className="delivery-input"
                          value={form.carrierName}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            carrierName: event.target.value
                          }))}
                          placeholder={elakayAutoText("h_8caa28e55c3be8", "Nom du livreur ou de la compagnie")}
                        />
                      </div>
                    )}

                    <div className="delivery-field">
                      <label>{elakayAutoText("h_329cb8b6ba8c42", "Service")}</label>
                      <input
                        className="delivery-input"
                        value={form.serviceLevel}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          serviceLevel: event.target.value
                        }))}
                        placeholder={elakayAutoText("h_5b6f3080491e1e", "Ground, Priority, Same day...")}
                      />
                    </div>

                    <div className="delivery-field">
                      <label>{elakayAutoText("h_9e94c3e4f34cf6", "Nombre de colis")}</label>
                      <input
                        className="delivery-input"
                        type="number"
                        min="1"
                        max="100"
                        value={form.packageCount}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          packageCount: event.target.value
                        }))}
                      />
                    </div>

                    <div className="delivery-field">
                      <label>{elakayAutoText("h_437b6035dd99af", "Livraison estimee")}</label>
                      <input
                        className="delivery-input"
                        type="datetime-local"
                        value={form.estimatedDeliveryAt}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          estimatedDeliveryAt: event.target.value
                        }))}
                      />
                    </div>

                    <div className="delivery-field delivery-field-full">
                      <label>{elakayAutoText("h_366af4f77e0d14", "URL de suivi")}</label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <input
                          className="delivery-input"
                          value={form.trackingUrl}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            trackingUrl: event.target.value
                          }))}
                          placeholder="https://..."
                        />
                        {form.trackingUrl && (
                          <a
                            className="delivery-btn delivery-btn-secondary"
                            href={form.trackingUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={elakayAutoText("h_1001a99d204baf", "Ouvrir le suivi")}
                          >
                            <FiExternalLink />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="delivery-section">
                  <div className="delivery-section-head">
                    <div className="delivery-section-title"><FiHash />{elakayAutoText("h_0bdcc83fb8c8a1", "Scan et evenement")}</div>
                    <div className="delivery-section-note">
                      {lastScan ? `Dernier scan: ${lastScan}` : elakayAutoText("h_3ebed6414fd37a", "Mise a jour manuelle")}
                    </div>
                  </div>

                  <div className="delivery-form-grid">
                    <div className="delivery-field">
                      <label>{elakayAutoText("h_49025bf4274e21", "Code scanne / saisie rapide")}</label>
                      <input
                        className="delivery-input"
                        value={lastScan}
                        onChange={(event) => setLastScan(event.target.value)}
                        placeholder={elakayAutoText("h_3833a231eff7f5", "Code-barres, QR ou reference")}
                      />
                    </div>
                    <div className="delivery-field">
                      <label>{elakayAutoText("h_178533f41f89b9", "Lieu du scan")}</label>
                      <input
                        className="delivery-input"
                        value={form.locationLabel}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          locationLabel: event.target.value
                        }))}
                        placeholder={elakayAutoText("h_7ce916cfa5fb23", "Entrepot, ville, porte...")}
                      />
                    </div>
                    <div className="delivery-field delivery-field-full">
                      <label>{elakayAutoText("h_39a91db29f081e", "Note de cet evenement")}</label>
                      <textarea
                        className="delivery-textarea"
                        value={form.eventNote}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          eventNote: event.target.value
                        }))}
                        placeholder={elakayAutoText("h_f4d8ff13e8c69b", "Etat du colis, instructions, circonstances...")}
                      />
                    </div>
                    {["exception", "attempted", "held"].includes(form.status) && (
                      <div className="delivery-field delivery-field-full">
                        <label>{elakayAutoText("h_753e7275236b41", "Raison de l'incident")}</label>
                        <input
                          className="delivery-input"
                          value={form.exceptionReason}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            exceptionReason: event.target.value
                          }))}
                          placeholder={elakayAutoText("h_9b8c0e16799b5e", "Adresse inaccessible, meteo, destinataire absent...")}
                        />
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="delivery-btn delivery-btn-secondary"
                      onClick={captureLocation}
                      disabled={locationBusy}
                    >
                      <FiMapPin />
                      {locationBusy ? elakayAutoText("h_643b462d30634f", "Localisation...") : elakayAutoText("h_9c74ad2465d239", "Ajouter ma position")}
                    </button>
                  </div>

                  {location && (
                    <div className="delivery-location" style={{ marginTop: 9 }}>
                      <FiMapPin />
                      <span>
                        {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
                        {" · "}{elakayAutoText("h_13800272d7a85c", "precision")}{Math.round(location.accuracy || 0)} m
                      </span>
                    </div>
                  )}
                </section>

                <section className="delivery-section">
                  <div className="delivery-section-head">
                    <div className="delivery-section-title"><FiUser />{elakayAutoText("h_d928bfee825c93", "Reception")}</div>
                    <div className="delivery-section-note">{elakayAutoText("h_bf9f0608a6c7ad", "Requis lorsque le statut est Livre")}</div>
                  </div>
                  <div className="delivery-form-grid">
                    <div className="delivery-field">
                      <label>{elakayAutoText("h_d71c55aa9a5e02", "Destinataire / personne presente")}</label>
                      <input
                        className="delivery-input"
                        value={form.recipientName}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          recipientName: event.target.value
                        }))}
                        placeholder={elakayAutoText("h_1ee93933079275", "Nom complet, reception, concierge...")}
                      />
                    </div>
                    <div className="delivery-field">
                      <label>{elakayAutoText("h_71825aebf94ef7", "Nom de signature")}</label>
                      <input
                        className="delivery-input"
                        value={form.signatureName}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          signatureName: event.target.value
                        }))}
                        placeholder={elakayAutoText("h_879d6b169db9d7", "Optionnel")}
                      />
                    </div>
                    <div className="delivery-field delivery-field-full">
                      <label>{elakayAutoText("h_30b3c38360a022", "Notes permanentes du livreur")}</label>
                      <textarea
                        className="delivery-textarea"
                        value={form.driverNotes}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          driverNotes: event.target.value
                        }))}
                        placeholder={elakayAutoText("h_56e50d2101256c", "Acces, code de porte, instruction client, etat final...")}
                      />
                    </div>
                  </div>
                </section>

                <section className="delivery-section">
                  <div className="delivery-section-head">
                    <div className="delivery-section-title"><FiCamera />{elakayAutoText("h_5172c6bb140ced", "Preuves photo")}</div>
                    <div className="delivery-section-note">{elakayAutoText("h_53e9ccf862b53c", "4 nouvelles photos par mise a jour, 12 maximum")}</div>
                  </div>

                  <div className="delivery-form-grid" style={{ marginBottom: 10 }}>
                    <div className="delivery-field">
                      <label>{elakayAutoText("h_12eb7893052bd6", "Type de preuve")}</label>
                      <select
                        className="delivery-select"
                        value={proofKind}
                        onChange={(event) => setProofKind(event.target.value)}
                      >
                        {PROOF_KINDS.map((kind) => (
                          <option value={kind.value} key={kind.value}>{kind.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="delivery-field">
                      <label>{elakayAutoText("h_6869e5b8d71baf", "Note pour les photos")}</label>
                      <input
                        className="delivery-input"
                        value={proofNote}
                        onChange={(event) => setProofNote(event.target.value)}
                        placeholder={elakayAutoText("h_a18c56f00790e8", "Porte avant, colis intact...")}
                      />
                    </div>
                  </div>

                  <div className="delivery-photo-grid">
                    {(selected.delivery?.proofs || []).map((proof) => (
                      <div className="delivery-photo" key={proof._id}>
                        {proofUrls[String(proof._id)] ? (
                          <img src={proofUrls[String(proof._id)]} alt={proof.kind} />
                        ) : (
                          <div className="delivery-empty" style={{ minHeight: "100%", padding: 8 }}>
                            <FiRefreshCw className="delivery-spin" />
                          </div>
                        )}
                        <button
                          type="button"
                          className="delivery-photo-remove"
                          onClick={() => removeExistingProof(proof._id)}
                          aria-label={elakayAutoText("h_db39f4180b6dd6", "Supprimer la preuve")}
                        >
                          <FiX />
                        </button>
                        <span className="delivery-photo-kind">
                          {PROOF_KINDS.find((kind) => kind.value === proof.kind)?.label || proof.kind}
                        </span>
                      </div>
                    ))}

                    {pendingPhotos.map((photo) => (
                      <div className="delivery-photo" key={photo.id}>
                        <img src={photo.url} alt={elakayAutoText("h_b843289c403de7", "Nouvelle preuve")} />
                        <button
                          type="button"
                          className="delivery-photo-remove"
                          onClick={() => removePendingPhoto(photo.id)}
                          aria-label={elakayAutoText("h_effa9e13e8283b", "Retirer la photo")}
                        >
                          <FiX />
                        </button>
                        <span className="delivery-photo-kind">{elakayAutoText("h_adbae3295bae4c", "Nouvelle ·")}{PROOF_KINDS.find((kind) => kind.value === photo.kind)?.label}
                        </span>
                      </div>
                    ))}

                    {pendingPhotos.length < 4 && (selected.delivery?.proofs?.length || 0) + pendingPhotos.length < 12 && (
                      <label className="delivery-upload">
                        <span>
                          <FiUpload size={23} />
                          <span>{elakayAutoText("h_ffd76632875cd0", "Camera ou galerie")}</span>
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                          capture="environment"
                          multiple
                          onChange={addPhotos}
                        />
                      </label>
                    )}
                  </div>
                </section>

                <section className="delivery-section">
                  <div className="delivery-section-head">
                    <div className="delivery-section-title"><FiClock />{elakayAutoText("h_34f3a06a7265f5", "Historique")}</div>
                    <div className="delivery-section-note">
                      {selected.delivery?.events?.length || 0}{elakayAutoText("h_a7ba559c93f873", "evenement(s)")}</div>
                  </div>
                  {(selected.delivery?.events || []).length ? (
                    <div className="delivery-timeline">
                      {selected.delivery.events.map((event) => (
                        <div className="delivery-event" key={event._id}>
                          <div className="delivery-event-dot">
                            {event.status === "delivered" ? <FiCheckCircle /> : <FiTruck />}
                          </div>
                          <div>
                            <div className="delivery-event-title">
                              {statusMeta(event.status).label}
                            </div>
                            <div className="delivery-event-meta">
                              {dateTime(event.occurredAt)}
                              {event.actorName ? ` · ${event.actorName}` : ""}
                              {event.source ? ` · ${event.source}` : ""}
                              {event.locationLabel ? ` · ${event.locationLabel}` : ""}
                            </div>
                            {(event.note || event.exceptionReason || event.scanCode) && (
                              <div className="delivery-event-note">
                                {event.note || event.exceptionReason}
                                {event.scanCode && (
                                  <div>{elakayAutoText("h_bc9271edc53cc9", "Scan:")}{event.scanCode}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="delivery-location">
                      <FiClock />{elakayAutoText("h_9e7b5c8667e2b3", "Aucun scan enregistre pour cet article.")}</div>
                  )}
                </section>

                <div className="delivery-actions">
                  <div className="delivery-actions-note">{elakayAutoText("h_8c57dbfa68e5a6", "La sauvegarde met aussi a jour le statut standard de la commande.")}</div>
                  <button
                    type="button"
                    className="delivery-btn delivery-btn-primary"
                    onClick={saveUpdate}
                    disabled={saving}
                  >
                    {saving ? (
                      <><FiRefreshCw className="delivery-spin" />{elakayAutoText("h_9bf1058a71405a", "Enregistrement...")}</>
                    ) : (
                      <><FiCheckCircle />{elakayAutoText("h_756f5790af05eb", "Enregistrer la mise a jour")}</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
        </div>
      </main>
    </Layout>
  );
}

export default Delivery;
